import { useEffect, useState } from "react";
import { Modal, Form, Button, Spinner, Alert } from "react-bootstrap";

interface Props {
  show: boolean;
  /** When set, we're adding a journey to an existing industry (locked). When null, we're adding a new industry. */
  presetIndustry: string | null;
  onClose: () => void;
  onCreate: (name: string, industry: string) => Promise<void>;
}

export default function AddJourneyModal({ show, presetIndustry, onClose, onCreate }: Props) {
  const isNewIndustry = presetIndustry === null;
  const [industry, setIndustry] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setIndustry(presetIndustry ?? "");
      setName("");
      setError(null);
    }
  }, [show, presetIndustry]);

  const handleCreate = async () => {
    if (!industry.trim() || !name.trim()) {
      setError("Please fill in both fields.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onCreate(name.trim(), industry.trim());
      onClose();
    } catch {
      setError("Failed to create. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="mb-0">
          {isNewIndustry ? "Add industry" : `Add journey to ${presetIndustry}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold small text-uppercase text-secondary">Industry</Form.Label>
          <Form.Control
            value={industry}
            disabled={!isNewIndustry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g. Healthcare"
            autoFocus={isNewIndustry}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="fw-semibold small text-uppercase text-secondary">Journey name</Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Continuing Education"
            autoFocus={!isNewIndustry}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={saving || !name.trim() || !industry.trim()}>
          {saving ? <><Spinner size="sm" animation="border" className="me-2" />Creating…</> : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
